import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, entityTypeNameFor } from '@shapeable/utils';
import { Document } from '@shapeable/doing-co-types';
import {
  DocumentModalLayout,
  EntityCard,
  EntityCardDefaultProps,
  EntityCardProps,
  EntityImage,
  LabelledValue,
  useModal,
  usePageEntity,
} from '@shapeable/ui';
import { NEUTRAL_BACKGROUND } from '../../theme';

const cls = classNames('document-card');

export type DocumentCardProps = Omit<EntityCardProps, 'entity'> & {
  entity?: Document;
};

export const DocumentCardDefaultProps: Omit<DocumentCardProps, 'entity'> = {
  ...EntityCardDefaultProps,
};

type ContainerProps = {
  _carouselStyles?: boolean;
  isClickable?: boolean;
  setMaxWidth?: boolean;
};

const ContainerStyles = breakpoints({
  base: css`
    min-width: 200px;
    ${theme.TRANSITION('scale')};

    &:hover {
      cursor: pointer;
    }

    ${({ setMaxWidth }: ContainerProps) => setMaxWidth && css`
      max-width: 350px;
    `}
  `,
});

const MetaStyles = breakpoints({
  base: css`
  `,
});

const My = {
  Container: styled(EntityCard)<ContainerProps>`${ContainerStyles}`,
  Folder: styled(LabelledValue)`${MetaStyles}`,
};

export const DocumentCard: Shapeable.FC<DocumentCardProps> = (props) => {
  const { className, children, entity, ...cardProps } = props;

  const { folder } = entity || {};
  const activePage = usePageEntity();
  const isDoingPage = entityTypeNameFor(activePage) === 'ImpactStory';
  const modal = useModal();

  const handleModalOpen = () => {
    modal?.show(
      <DocumentModalLayout entity={entity} />,
      { color: '#000000', title: 'Document', opacity: 0.5 },
    );
  };

  return (
    <My.Container
      {...cardProps}
      className={cls.name(className)}
      showColorStrip
      colorStripColor="primary"
      colorStripSize={2}
      showImage={true}
      useLink={false}
      onClick={handleModalOpen}
      useEntityColorForBackground={true}
      showDescription={true}
      labelDeriver={() => entity?.type?.name}
      setMaxWidth={isDoingPage}
      entity={entity}
      image={
        <EntityImage
          entity={entity}
          colors={{ background: NEUTRAL_BACKGROUND, fill: 'primary' }}
        />
      }
      meta={
        !!folder && (
          <My.Folder
            label="Folder"
            value={folder?.name}
          />
        )
      }
    >
      {children}
    </My.Container>
  );
};

DocumentCard.defaultProps = DocumentCardDefaultProps;
DocumentCard.cls = cls;
